import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cx from 'clsx'
import { graphql, Link, navigate } from 'gatsby'
import { ModalRoutingContext } from 'gatsby-plugin-modal-routing'
import SVG from 'react-inlinesvg'

// Material-UI
import Container from '@material-ui/core/Container'
// Components
import Header from '~2023/components/Header'
import Footer from '~2023/components/Footer'
import FandomCardDetail from '~2023/components/FandomCardDetail'
import Seo from '~2023/components/Seo'
import { Timeline } from '~2023/components/Timeline'
import { CloseIcon } from '~2023/components/Icons'

// Redux
import { loadFilter, setModal, setSort } from '~lib/saga/filter/actions'

// Utils
import { categoryIcons, getFandom } from '~2023/utils'

const filterSelector = (state) => state.filter

import { makeStyles } from '@material-ui/core/styles'
import { OutboundLink } from 'react-ga'
import { sendGTMEvent } from '../../../utils/googleAnalytics'

import useSiteMeta from '~2023/hooks/useSiteMeta'

const useStyles = makeStyles((theme) => ({
  blackBackground: {
    maxWidth: 768,
    margin: '0 auto',
    clipPath: 'polygon(100% 0, 100% 100%, 50% 96%, 0 100%, 0 0)',
  },
  categoryColorBackground: {
    position: 'relative',
    background: ({ categoryColor = 'fff' }) => `#${categoryColor}`,
    border: ({ categoryColor = 'fff' }) => `1px solid #${categoryColor}`,
    borderTop: ({ categoryColor = 'fff' }) => `4px solid #${categoryColor}`,
    padding: '0px 1px',
  },
  innerBlackBackground: {
    maxWidth: 764,
    margin: '0 auto',
    top: '-2px',
    position: 'relative',
    background: 'black',
    clipPath: 'polygon(100% 0, 100% 100%, 50% 96%, 0 100%, 0 0)',
    borderTop: '4px solid black',
    padding: '0px 4px',
  },
  clipEnd: {
    background: '#111',
    height: '6rem',
    clipPath: 'polygon(100% 0, 100.00% 95%, 50% 46%, 0 95%, 0 0)',
    [theme.breakpoints.down('xs')]: {
      clipPath: 'polygon(100% 0, 100.00% 96%, 50% 46%, 0 96%, 0 0)',
    },
  },
  blurbAuthor: {
    marginTop: '1rem',
    fontWeight: 'bold',
    color: `rgb(${theme.palette.dustyGray})`,
    fontSize: '1rem',
  },
  blurbAuthorTitle: {
    fontSize: '1rem',
    color: `rgb(${theme.palette.doveGray})`,
  },
  subheading: {
    fontSize: '1.2rem',
    marginBottom: '.7rem',
    paddingTop: '.7rem',
  },
  categorySub: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      marginTop: '1rem',
    },
  },
  cardContentWrapper: {
    position: 'relative',
    padding: '1rem 1rem 0 1rem;',
  },
  titleContainer: {
    position: 'relative',
    top: 0,
    [theme.breakpoints.down('xs')]: {
      top: '-8rem',
    },
  },
  superlativeContainer: {
    top: '-5rem',
    left: '-1.25rem',
    zIndex: 10,
    position: 'absolute',
    fontFamily: 'abolition, Arial, Helvetica, sans-serif',
    color: '#fff',
    [theme.breakpoints.down('xs')]: {
      top: ({ isTwoLineTitleFromElement }) =>
        isTwoLineTitleFromElement ? '-3rem' : '0',
    },
  },
  titleLineFirstOfTwo: {
    top: 0,
    [theme.breakpoints.down('xs')]: {
      top: '-.5rem !important',
    },
  },
}))

// eslint-disable-next-line no-unused-vars
const Fandom = ({ data, location, pageContext }) => {
  const dispatch = useDispatch()

  const { categoryNext, categoryPrevious, overallNext, overallPrevious } =
    pageContext

  const {
    data: {
      id: filterId,
      isFiltered: isFiltered,
      sort: filterSort,
      title: filterTitle,
      year: filterYear,
    },
  } = useSelector(filterSelector)
  const { siteUrl } = useSiteMeta()

  const { markdownRemark: node } = data
  const item = getFandom({
    filterId,
    filterTitle,
    isFiltered,
    item: node,
  })

  const categoryIcon =
    categoryIcons[
      Array.isArray(item.categoryId) ? item.categoryId[0] : item.categoryId
    ]

  const fromModal =
    location.state && location.state.fromModal
      ? location.state.fromModal
      : false

  const withSorting = 'rank' === filterSort ? '' : `?sort=${filterSort}`

  const titleRef = useRef(null)
  const [isTwoLineTitleFromElement, setIsTwoLineTitleFromElement] =
    useState(false)
  const [init, setInit] = useState(false)
  useEffect(() => {
    if (init && titleRef.current) {
      const isTwoLineTitle =
        60 < getComputedStyle(titleRef.current).height.split('px')[0]
      setIsTwoLineTitleFromElement(isTwoLineTitle)
    }
    setInit(true)
  }, [init])

  const styles = useStyles({
    categoryColor: item.categoryHex,
    isTwoLineTitleFromElement,
  })

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  //   Track pageview and number of clicks
  useEffect(() => {
    sendGTMEvent('', '', 'pageview')
    sendGTMEvent(siteUrl + item.path)
  }, [siteUrl, item.path])

  const handleFilterLoad = (event, id) => {
    event.preventDefault()
    dispatch(loadFilter(id))
    dispatch(setModal(false))
    // @note[request] If we move between categories, force `rank`
    dispatch(setSort('rank'))
    navigate(`/${filterYear}/${id}`)
  }
  return (
    <ModalRoutingContext.Consumer>
      {({ modal, closeTo = location.state.closeToLink || null }) => (
        <>
          {modal || fromModal ? (
            <Container
              maxWidth="xl"
              style={
                fromModal || modal
                  ? {
                      height: '6rem',
                      marginBottom: '-6rem',
                      maxWidth: '946px',
                      position: 'relative',
                    }
                  : {
                      maxWidth: '946px',
                      position: 'relative',
                    }
              }
            >
              <Link
                to={
                  (closeTo
                    ? closeTo.replace('/fandoms-of-the-year/', '/')
                    : '/') + withSorting
                }
                state={{ activeDetail: item.rank, noScroll: true }}
                style={{ position: 'absolute', right: '40px', top: '40px' }}
              >
                <SVG
                  description="Go back to latest Fandom Listing"
                  height={15}
                  src={CloseIcon}
                  width={14}
                />
              </Link>
            </Container>
          ) : (
            <Header />
          )}
          <Seo
            title={item.title}
            detailFandomRank={item.categoryRank}
            detailFandomTitle={item.title}
            detailFilterTitle={item.categoryTitle}
            twitterHandle={item.twitterHandle}
            path={`/${filterYear}/${item.path}`}
            detailImage={item.imageSocial}
            isFandom
          />
          <main
            itemScope
            itemProp="mainContentOfPage"
            className={`id-${item.categoryId}`}
          >
            <Container maxWidth="xl">
              <div className="detail-cardDetailWrapper">
                <AddColoredBorder styles={styles}>
                  <article
                    className={`detail-cardContainer`}
                    itemScope
                    itemType="https://schema.org/BlogPosting"
                  >
                    <header className="detail-card">
                      <FandomCardDetail item={item} />
                    </header>

                    <div className={styles.cardContentWrapper}>
                      <div className={styles.titleContainer}>
                        {item.superlative && (
                          <div
                            className={styles.superlativeContainer}
                            itemProp="articleSection"
                          >
                            <p className="detail-cardMeta detail-cardMeta-__foty_redesign card--meta">
                              {item.superlative}
                            </p>
                          </div>
                        )}
                        <h1
                          ref={titleRef}
                          className={`detail-cardTitle detail-cardTitle-__foty_redesign ${
                            isTwoLineTitleFromElement
                              ? styles.titleLineFirstOfTwo
                              : ''
                          }`}
                        >
                          {item.title}
                        </h1>
                      </div>

                      <hr className="detail-hr-__foty_redesign" />

                      <div className={styles.categorySub}>
                        <SVG
                          className="detail-svg"
                          src={categoryIcon}
                          width={48}
                          height={32}
                        />

                        <h5 className="detail-contentTypeContainer detail-contentTypeContainer-__foty_redesign">
                          SEE ALL{' '}
                          {'fandomOfTheYear' === item.categoryId?.[0] ? (
                            <Link
                              className={cx(
                                'detail-cardCategoryLink-__foty_redesign',
                                item.categoryPrimary
                              )}
                              onClick={(event) =>
                                handleFilterLoad(event, [item.categoryPrimary])
                              }
                              to={`/${filterYear}/${item.categoryPrimary}`}
                            >
                              TV
                            </Link>
                          ) : (
                            <Link
                              className={cx(
                                'detail-cardCategoryLink-__foty_redesign',
                                item.categoryId
                              )}
                              onClick={(event) =>
                                handleFilterLoad(event, [item.categoryId])
                              }
                              to={`/${filterYear}/${item.categoryId}`}
                            >
                              {item.categoryTitle}
                            </Link>
                          )}
                        </h5>
                      </div>

                      <div
                        className={cx(
                          'detail-content',
                          'card--content-__foty_redesign'
                        )}
                        itemProp="articleBody"
                      >
                        <div>{item.topBlurb}</div>
                        <div className={styles.blurbAuthor}>
                          - {item.blurbAuthorName}
                        </div>
                        <div className={styles.blurbAuthorTitle}>
                          {item.blurbAuthorTitle}
                        </div>
                        {item.blurbAuthorLinkText && (
                          <div className={styles.blurbAuthorTitle}>
                            <OutboundLink
                              className={styles.blurbAuthorTitle}
                              to={item.blurbAuthorLinkUrl}
                              eventLabel={item.blurbAuthorLinkUrl}
                              rel="noopener noreferrer nofollow"
                              target="_blank"
                            >
                              {item.blurbAuthorLinkText}
                            </OutboundLink>
                            {item.secondBlurbAuthorLinkText && ' • '}
                            {item.secondBlurbAuthorLinkText && (
                              <OutboundLink
                                className={styles.blurbAuthorTitle}
                                to={item.secondBlurbAuthorLinkUrl}
                                eventLabel={item.secondBlurbAuthorLinkUrl}
                                rel="noopener noreferrer nofollow"
                                target="_blank"
                              >
                                {item.secondBlurbAuthorLinkText}
                              </OutboundLink>
                            )}
                          </div>
                        )}
                      </div>

                      <div
                        className={cx(
                          'detail-content',
                          'card--content-__foty_redesign'
                        )}
                        itemProp="articleBody"
                      >
                        <h2 className={styles.subheading}>
                          {item.secondBlurbSubhead}
                        </h2>
                        <div>{item.secondBlurbText}</div>
                      </div>

                      <div
                        className={cx(
                          'detail-content',
                          'card--content-__foty_redesign'
                        )}
                        itemProp="articleBody"
                      >
                        <h2 className={styles.subheading}>
                          {item.thirdBlurbSubhead}
                        </h2>
                        <div>{item.thirdBlurbText}</div>
                      </div>
                    </div>

                    <div
                      itemProp="publisher"
                      itemScope="itemscope"
                      itemType="https://schema.org/Organization"
                    >
                      <meta itemProp="name" content="Fansided" />
                    </div>
                  </article>
                </AddColoredBorder>

                {'rank' === filterSort && (
                  <Timeline
                    categoryNext={categoryNext}
                    categoryPrevious={categoryPrevious}
                    overallNext={overallNext}
                    overallPrevious={overallPrevious}
                    fromModal={modal || fromModal}
                    closeTo={closeTo || '/'}
                    isDetail
                  />
                )}
              </div>
            </Container>
          </main>
          <Footer
            detailFandomRank={item.categoryRank}
            detailFandomTitle={item.title}
            detailFilterTitle={item.categoryTitle}
            twitterHandle={item.twitterHandle}
          />
        </>
      )}
    </ModalRoutingContext.Consumer>
  )
}

export const pageQuery = graphql`
  query ($path: String!) {
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      html
      fields {
        allEntertainment
        allSports
        # brands
        celebrities
        college
        comics
        gaming
        mlb
        movies
        music
        nfl
        nhl
        other
        proBasketball
        soccer
        sportsFigures
        tv
      }
      frontmatter {
        categories
        categoryPrimary
        contentType
        imageCard
        imageDetail
        imageSocial
        path
        title
        twitterHandle

        topBlurb
        blurbAuthorName
        blurbAuthorTitle
        blurbAuthorLinkText
        blurbAuthorLinkUrl
        secondBlurbAuthorLinkText
        secondBlurbAuthorLinkUrl
        secondBlurbSubhead
        secondBlurbText
        thirdBlurbSubhead
        thirdBlurbText
        superlative
      }
    }
  }
`

export default Fandom

function AddColoredBorder({ children, styles }) {
  return (
    <div className={styles.blackBackground}>
      <div className={styles.categoryColorBackground}>
        <div className={styles.innerBlackBackground}>
          {children}
          <div className={styles.clipEnd} />
        </div>
      </div>
    </div>
  )
}
